import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { useCallback } from 'react';
import { cn } from '../../../lib/styles';
import { Icon } from '../icon/icon.spark';
import { Text } from '../index.spark';
import { Tooltip } from '../tooltip/tooltip.spark';
import type { DropdownOption, DropdownProps } from './types';

function Option(props: DropdownOption & { inGroup?: boolean }) {
  return (
    <ListboxOption
      value={props.value}
      disabled={props.disabled}
      className={cn(
        'cursor-pointer px-2 py-1 font-normal data-disabled:cursor-not-allowed data-focus:bg-gray-100 data-selected:font-semibold data-disabled:text-spark-text-secondary',
        { 'ps-4': props.inGroup },
      )}
    >
      {props.label}
    </ListboxOption>
  );
}

export function Dropdown(props: DropdownProps) {
  const getOption = useCallback(
    (value: string) => {
      for (const option of props.options) {
        // Option group
        if ('options' in option) {
          const found = option.options.find((childOption) => childOption.value === value);
          if (found) {
            return found;
          }

          continue;
        }

        if (option.value === value) {
          return option;
        }
      }

      return undefined;
    },
    [props.options],
  );

  return (
    <Listbox
      as="div"
      value={props.value}
      onChange={props.onChange}
      disabled={props.disabled}
      id={props.id}
    >
      {({ open }) => (
        <div className="flex shrink-0 flex-col gap-1.5 text-sm text-spark-text-primary">
          {props.label && (
            <Label className="flex items-center gap-x-1">
              {props.label}
              {props.tooltip && (
                <Tooltip content={props.tooltip}>
                  <Icon name="infoCircle" />
                </Tooltip>
              )}
            </Label>
          )}
          <div className="flex flex-col gap-1">
            <ListboxButton
              className={cn(
                'flex h-spark-component w-full shrink-0 items-center justify-between rounded-lg border border-gray-300 bg-white px-2 text-sm shadow-xs focus:border-spark-purple focus:outline-hidden disabled:bg-gray-100 disabled:text-spark-text-secondary',
                {
                  'text-spark-text-secondary': !props.value,
                  'border-red-300 bg-red-100 focus:border-spark-text-danger': !!props.error,
                },
              )}
            >
              <span className="truncate">
                {props.value ? getOption(props.value)?.label : props.placeholder}
              </span>
              <Icon name="chevronDown" />
            </ListboxButton>

            <Transition
              show={open}
              leave="duration-100 transition"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              enter="duration-100 transition"
              enterFrom="opacity-0"
              enterTo="opacity-100"
            >
              <ListboxOptions
                anchor="bottom"
                className="z-30 max-h-60 min-w-(--button-width) overflow-auto rounded-md border border-gray-300 bg-white py-1 text-sm text-spark-text-primary shadow-lg [--anchor-gap:4px] focus:outline-hidden"
              >
                {!props.options.length && (
                  <span className="px-2 py-1">
                    <Text variant="secondary">{props.noOptionsMessage}</Text>
                  </span>
                )}
                {props.options.map((option) => {
                  // Option group
                  if ('options' in option) {
                    return (
                      <div key={option.title}>
                        <span className="inline-flex px-2 pt-1">
                          <Text variant="secondary" size="sm">
                            {option.title}
                          </Text>
                        </span>
                        {option.options.map((childOption) => (
                          <Option
                            key={childOption.key ?? childOption.value}
                            inGroup
                            {...childOption}
                          />
                        ))}
                      </div>
                    );
                  }

                  return <Option key={option.key ?? option.value} {...option} />;
                })}
              </ListboxOptions>
            </Transition>
          </div>
          {props.error && (
            <span className="flex items-center gap-0.5 text-spark-text-danger">
              <Icon name="xCircle" />
              <p className="text-xs">{props.error}</p>
            </span>
          )}
        </div>
      )}
    </Listbox>
  );
}
